/**
 * The page signals Inventory's Pages tab and URL detail show. No pair signal: the retired engine's
 * "Cannibal" chip went with it (quality sprint 7 F); which pages compete is the Cannibalization card
 * on `/review` and the Issues queue's pair items, both built on one engine.
 */
export type InventoryPageSignalKind = 'internal_link' | 'no_cluster' | 'unclustered';

export interface InventoryPageSignal {
  kind: InventoryPageSignalKind;
  label: string;
  title: string;
}

export type ActionPlanRowStatus = 'open' | 'done' | 'dismissed';

export interface InternalLinkSignalRow {
  status: ActionPlanRowStatus;
  sourceUrlId: string;
  targetUrlId: string;
}

export interface UnclusteredSignalRow {
  status: ActionPlanRowStatus;
  urlId: string;
}

export interface PageSignalSourceRow {
  urlId: string;
  clusters?: string[];
}

const SIGNAL_ORDER: InventoryPageSignalKind[] = [
  'internal_link',
  'unclustered',
  'no_cluster'
];

export function buildPageSignalsForUrl(
  urlId: string,
  clusters: string[] | undefined,
  internalLinkRows: InternalLinkSignalRow[],
  unclusteredRows: UnclusteredSignalRow[]
): InventoryPageSignal[] {
  const normalizedId = (urlId || '').trim();
  if (!normalizedId) return [];

  const signals: InventoryPageSignal[] = [];

  const openInternalLink = internalLinkRows.some(
    (row) => row.status === 'open' && row.targetUrlId === normalizedId
  );
  if (openInternalLink) {
    signals.push({
      kind: 'internal_link',
      label: 'Link',
      title: 'Open internal-link action-plan suggestion involving this URL'
    });
  }

  const openUnclustered = unclusteredRows.some((row) => row.status === 'open' && row.urlId === normalizedId);
  if (openUnclustered) {
    signals.push({
      kind: 'unclustered',
      label: 'Unclustered',
      title: 'Open unclustered-page action-plan suggestion for this URL'
    });
  }

  const clusterLabels = Array.isArray(clusters) ? clusters.filter((c) => typeof c === 'string' && c.trim()) : [];
  if (clusterLabels.length === 0) {
    signals.push({
      kind: 'no_cluster',
      label: 'No cluster',
      title: 'This URL is not assigned to any workspace plan cluster'
    });
  }

  return signals.sort((a, b) => SIGNAL_ORDER.indexOf(a.kind) - SIGNAL_ORDER.indexOf(b.kind));
}

export function buildPageSignalsIndex(
  pages: PageSignalSourceRow[],
  internalLinkRows: InternalLinkSignalRow[],
  unclusteredRows: UnclusteredSignalRow[]
): Map<string, InventoryPageSignal[]> {
  const index = new Map<string, InventoryPageSignal[]>();
  for (const page of pages) {
    const urlId = (page.urlId || '').trim();
    if (!urlId) continue;
    index.set(
      urlId,
      buildPageSignalsForUrl(urlId, page.clusters, internalLinkRows, unclusteredRows)
    );
  }
  return index;
}
